import { ArrowLeft, Calendar, ExternalLink, Globe, MapPin, Newspaper, Store } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import BONavigation from "components/admin/BONavigation";
import { AnalysisAge, AnalysisCheckButton } from "components/franchise/AnalysisCheck";
import { getFranchise, getFranchiseEstablishments, updateFranchise, FranchiseDetail, FranchiseDoc, FranchiseEstablishment } from "interfaces/establishments";

// ─── helpers ──────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3_600_000);
    if (h < 1) return "À l'instant";
    if (h < 24) return `Il y a ${h}h`;
    const d = Math.floor(h / 24);
    if (d === 1) return "Hier";
    if (d < 7) return `Il y a ${d}j`;
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
    pending:  { label: "En attente", cls: "bg-gray-100 text-gray-600" },
    approved: { label: "Approuvé",   cls: "bg-emerald-50 text-emerald-700" },
    rejected: { label: "Rejeté",     cls: "bg-red-50 text-red-600" },
};

const CATEGORY_LABEL: Record<string, { label: string; color: string }> = {
    opening:     { label: "Ouverture",   color: "text-emerald-700" },
    closing:     { label: "Fermeture",   color: "text-red-700" },
    ownership:   { label: "Cession",     color: "text-amber-700" },
    recruitment: { label: "Recrutement", color: "text-sky-700" },
};

// ─── inline field editor ──────────────────────────────────────────────────────

function EditableField({
    label,
    value,
    multiline,
    onSave,
}: {
    label: string;
    value: string | null;
    multiline?: boolean;
    onSave: (v: string | null) => Promise<void>;
}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value ?? "");
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try { await onSave(draft.trim() || null); setEditing(false); } finally { setSaving(false); }
    };

    if (editing) {
        return (
            <div className="flex flex-col gap-1">
                <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold">{label}</span>
                {multiline ? (
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        rows={3}
                        autoFocus
                        className="text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-400 resize-none"
                    />
                ) : (
                    <input
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
                        autoFocus
                        className="text-xs border border-gray-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                )}
                <div className="flex gap-2">
                    <button onClick={save} disabled={saving}
                        className="text-xs font-medium text-white bg-black px-2.5 py-1 rounded-lg disabled:opacity-50">
                        Enregistrer
                    </button>
                    <button onClick={() => setEditing(false)}
                        className="text-xs text-gray-500 hover:text-gray-800 px-2.5 py-1 rounded-lg hover:bg-gray-100">
                        Annuler
                    </button>
                </div>
            </div>
        );
    }

    return (
        <button onClick={() => setEditing(true)} className="text-left group">
            <span className="text-[10px] text-gray-400 uppercase tracking-wide font-semibold block mb-0.5">{label}</span>
            <span className={`text-xs ${value ? "text-gray-700" : "text-gray-400 italic"} group-hover:text-black transition-colors`}>
                {value ?? `Ajouter ${label.toLowerCase()}…`}
            </span>
        </button>
    );
}

// ─── main ─────────────────────────────────────────────────────────────────────

export default function FranchiseDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [data, setData] = useState<FranchiseDetail | null>(null);
    const [loading, setLoading] = useState(true);
    // The fiche's rows are paged server-side now; this admin view keeps its first 100 as before.
    const [establishments, setEstablishments] = useState<FranchiseEstablishment[]>([]);

    useEffect(() => {
        if (!id) return;
        getFranchise(id).then(setData).finally(() => setLoading(false));
        getFranchiseEstablishments(id, { limit: 100 }).then((res) => setEstablishments(res.items)).catch(() => setEstablishments([]));
    }, [id]);

    const handleUpdate = async (field: keyof Omit<FranchiseDoc, "_id" | "name">, value: string | null) => {
        if (!data || !id) return;
        const updated = await updateFranchise(id, { [field]: value });
        setData((prev) => prev ? { ...prev, franchise: updated } : prev);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#F5F0EB]">
                <div className="mx-auto max-w-5xl px-4 py-6">
                    <BONavigation />
                    <div className="animate-pulse space-y-4 mt-4">
                        <div className="h-6 bg-gray-200 rounded-full w-1/3" />
                        <div className="h-4 bg-gray-100 rounded-full w-1/2" />
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[#F5F0EB] flex items-center justify-center">
                <p className="text-sm text-gray-500">Franchise introuvable</p>
            </div>
        );
    }

    const { franchise, news, counts } = data;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-5xl px-4 py-6">
                {/* back */}
                <button onClick={() => navigate("/bo/franchises")}
                    className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-black mb-5 transition-colors">
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Toutes les franchises
                </button>

                <BONavigation />

                {/* header */}
                <div className="flex gap-5 mb-8">
                    {/* logo */}
                    <div className="flex-shrink-0">
                        {franchise.logoUrl ? (
                            <img src={franchise.logoUrl} alt="" className="w-16 h-16 rounded-xl object-cover border border-gray-200"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        ) : (
                            <div className="w-16 h-16 rounded-xl bg-purple-50 flex items-center justify-center">
                                <span className="text-2xl font-semibold text-purple-600">{franchise.name[0]?.toUpperCase()}</span>
                            </div>
                        )}
                    </div>

                    {/* info */}
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-semibold text-black mb-1">{franchise.name}</h1>
                        {franchise.website && (
                            <a href={franchise.website} target="_blank" rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-gray-700 hover:underline mb-2">
                                <Globe className="w-3.5 h-3.5" />
                                {franchise.website.replace(/^https?:\/\//, "")}
                            </a>
                        )}
                        <p className="text-xs text-gray-500">
                            {news.length} article{news.length > 1 ? "s" : ""} · {counts.establishments} établissement{counts.establishments > 1 ? "s" : ""}
                        </p>
                    </div>

                    {/* last analysis */}
                    <div className="flex flex-shrink-0 items-start gap-2">
                        <div className="text-right">
                            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">Dernière analyse</span>
                            <AnalysisAge date={franchise.lastAnalyzedAt} />
                        </div>
                        <AnalysisCheckButton
                            franchiseId={franchise._id}
                            date={franchise.lastAnalyzedAt}
                            onChange={(lastAnalyzedAt) =>
                                setData((prev) => prev ? { ...prev, franchise: { ...prev.franchise, lastAnalyzedAt } } : prev)}
                        />
                    </div>
                </div>

                {/* banner image */}
                {franchise.imageUrl && (
                    <div className="mb-6 h-32 rounded-xl overflow-hidden bg-gray-50">
                        <img src={franchise.imageUrl} alt="" className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    </div>
                )}

                {/* editable fields */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-xl mb-8">
                    <EditableField label="Description" value={franchise.description} multiline
                        onSave={(v) => handleUpdate("description", v)} />
                    <EditableField label="Site web" value={franchise.website}
                        onSave={(v) => handleUpdate("website", v)} />
                    <EditableField label="Logo URL" value={franchise.logoUrl}
                        onSave={(v) => handleUpdate("logoUrl", v)} />
                    <EditableField label="Image URL" value={franchise.imageUrl}
                        onSave={(v) => handleUpdate("imageUrl", v)} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* news */}
                    <section>
                        <h2 className="text-sm font-semibold text-black mb-3 flex items-center gap-2">
                            <Newspaper className="w-4 h-4 text-gray-400" />
                            Articles ({news.length})
                        </h2>
                        {news.length === 0 ? (
                            <p className="text-xs text-gray-400">Aucun article lié</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {news.map((item) => {
                                    const status = STATUS_BADGE[item.status] ?? STATUS_BADGE.pending;
                                    const cat = CATEGORY_LABEL[item.category];
                                    return (
                                        <div key={item._id} className="flex gap-3 py-2.5">
                                            {item.image && (
                                                <img src={item.image} alt="" className="w-10 h-8 rounded-md object-cover flex-shrink-0 bg-gray-100"
                                                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                                                    {cat && <span className={`text-[10px] font-semibold ${cat.color}`}>{cat.label}</span>}
                                                    {item.city && (
                                                        <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                                                            <MapPin className="w-2.5 h-2.5" />{item.city}
                                                        </span>
                                                    )}
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${status.cls}`}>{status.label}</span>
                                                    <span className="text-[10px] text-gray-400">{timeAgo(item.published)}</span>
                                                </div>
                                                <p className="text-xs text-gray-800 leading-snug line-clamp-2">{item.title}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {/* establishments */}
                    <section>
                        <h2 className="text-sm font-semibold text-black mb-3 flex items-center gap-2">
                            <Store className="w-4 h-4 text-gray-400" />
                            Établissements ({counts.establishments})
                        </h2>
                        {establishments.length === 0 ? (
                            <p className="text-xs text-gray-400">Aucun établissement lié</p>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {establishments.map((est) => (
                                    <div key={est._id} className="flex items-center gap-2 py-2.5">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium text-gray-800 truncate">{est.name}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                {est.address?.city && (
                                                    <span className="text-[11px] text-gray-400 flex items-center gap-0.5">
                                                        <MapPin className="w-2.5 h-2.5" />
                                                        {est.address.city}{est.address.postcode ? ` (${est.address.postcode})` : ""}
                                                    </span>
                                                )}
                                                {est.operations?.opening_date && (
                                                    <span className="text-[11px] text-gray-400 flex items-center gap-0.5">
                                                        <Calendar className="w-2.5 h-2.5" />
                                                        {new Date(est.operations.opening_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <a href={`/establishments/${est._id}`} target="_blank" rel="noopener noreferrer"
                                            className="p-1 text-gray-300 hover:text-gray-600 transition-colors flex-shrink-0"
                                            title="Ouvrir l'établissement">
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}
